import { useSearchParams } from "react-router-dom";
import { useKnowledgeBasePublic, getArticleById } from "../../hooks/useKnowledgeBase";
import { KnowledgeCategoryGrid } from "../../components/psychologist/home/KnowledgeCategoryGrid";
import { KnowledgeCategoryView } from "../../components/psychologist/home/KnowledgeCategoryView";
import { KnowledgeArticleView } from "../../components/psychologist/home/KnowledgeArticleView";
import { HomeSkeleton } from "../../components/psychologist/home/HomeSkeleton";

/**
 * Preview público (sin login) del contenido de la knowledge base — pensado
 * para que el backoffice arme un link directo a una categoría/subcategoría/
 * artículo puntual sin depender de una sesión de psicóloga.
 *
 * Mismos query params que la Home real (`?category=`, `?subcategory=`,
 * `?article=`) y mismos componentes de vista — solo cambia la fuente de
 * datos (`useKnowledgeBasePublic`, contra `/api/knowledge-base/public`, sin
 * auth) y que no vive dentro de `AppShell`.
 */
export const KnowledgeBasePreviewPage = () => {
  const { categories, isLoading, error } = useKnowledgeBasePublic();
  const [searchParams, setSearchParams] = useSearchParams();

  const articleId = searchParams.get("article");
  const categoryId = searchParams.get("category");
  const subcategoryId = searchParams.get("subcategory");

  const goToCategory = (id: string) => setSearchParams({ category: id });
  const goToSubcategory = (id: string) =>
    setSearchParams(categoryId ? { category: categoryId, subcategory: id } : { subcategory: id });
  const goToCategoryAndSubcategory = (catId: string, subId: string) =>
    setSearchParams({ category: catId, subcategory: subId });
  const goToArticle = (id: string) => setSearchParams({ article: id });
  const goHome = () => setSearchParams({});

  const renderContent = () => {
    if (isLoading) return <HomeSkeleton />;

    if (error) {
      return <p className="text-white/70 text-center py-16">{error}</p>;
    }

    if (articleId) {
      const article = getArticleById(categories, articleId);
      if (!article) {
        return <p className="text-white/70 text-center py-16">No se encontró el artículo &quot;{articleId}&quot;.</p>;
      }
      // "Volver" va a la categoría/subcategoría real del artículo (buscada en
      // el árbol), no a la de la URL — un deep link directo (?article=X) no
      // trae category/subcategory, así que confiar en esos params dejaba
      // "Volver" sin a dónde ir más que la grilla completa.
      const goBackFromArticle = () => {
        for (const category of categories) {
          for (const sub of category.subcategories ?? []) {
            if (sub.articles.some((a) => a.id === articleId)) {
              return goToCategoryAndSubcategory(category.id, sub.id);
            }
          }
          if (category.articles.some((a) => a.id === articleId)) {
            return goToCategory(category.id);
          }
        }
        return goHome();
      };
      return <KnowledgeArticleView article={article} onBack={goBackFromArticle} />;
    }

    if (categoryId) {
      const category = categories.find((c) => c.id === categoryId);
      if (!category) {
        return <p className="text-white/70 text-center py-16">No se encontró la categoría &quot;{categoryId}&quot;.</p>;
      }
      const subcategory = subcategoryId
        ? category.subcategories?.find((s) => s.id === subcategoryId)
        : undefined;
      return (
        <KnowledgeCategoryView
          category={category}
          subcategory={subcategory}
          onSelectArticle={goToArticle}
          onSelectSubcategory={goToSubcategory}
          onBack={subcategory ? () => goToCategory(categoryId) : goHome}
        />
      );
    }

    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white">Explorar por categoría</h2>
        <KnowledgeCategoryGrid categories={categories} onSelectCategory={goToCategory} />
      </div>
    );
  };

  return (
    <div className="bg-[#423568] min-h-screen px-4 sm:px-8 pt-8 pb-16">
      <div className="max-w-3xl mx-auto space-y-4">
        <p className="text-white/40 text-xs uppercase tracking-wide text-center">
          Preview — Knowledge Base
        </p>
        {renderContent()}
      </div>
    </div>
  );
};
